import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate as useRouterNavigate } from 'react-router-dom';
import { api } from '../api';
import ClipDetailPopup from '../components/clips/ClipDetailPopup';
import JobClipGroup from '../components/clips/JobClipGroup';
import { PencilSquareIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Film, Sparkles } from 'lucide-react'; // Import new icons
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext'; // Import useSocket

// VOD bilgilerini de içerecek şekilde arayüzü güncelle
export interface ClipGalleryItem {
  id: string;
  title: string | null;
  thumbnailPath: string | null;
  filePath: string;
  jobId: string;
  vodTitle: string | null; // Eklendi
  vodThumbnailUrl: string | null; // Eklendi
  vodUrl: string | null; // Eklendi for platform detection
  llmOutputId: string | null;
  startTime: number | null;
  endTime: number | null;
  duration: number | null;
  createdAt: string;
  type: 'original' | 'edited';
  originalClipId?: string;
}

// Klipleri jobId'ye göre gruplayan bir arayüz
interface JobGroup {
  jobId: string;
  vodTitle: string | null;
  vodThumbnailUrl: string | null;
  vodUrl: string | null;
  clips: ClipGalleryItem[];
  hasEditedClips: boolean; // Yeni alan
}

const ClipGalleryPage: React.FC = () => {
  const [jobGroups, setJobGroups] = useState<JobGroup[]>([]); // Artık klipleri değil, iş gruplarını tutuyoruz
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClip, setSelectedClip] = useState<ClipGalleryItem | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  // const { galleryRefreshRequested, clearGalleryRefreshRequest } = useAuth(); // Bu özellikler kaldırıldı
  const location = useLocation();
  const routerNavigate = useRouterNavigate();

  const [activeCategory, setActiveCategory] = useState<'original' | 'edited'>('original');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedClipIds, setSelectedClipIds] = useState<string[]>([]);

  const groupClipsByJob = (clips: ClipGalleryItem[]): JobGroup[] => {
    const groups: { [key: string]: JobGroup } = {};
    clips.forEach(clip => {
      if (!groups[clip.jobId]) {
        groups[clip.jobId] = {
          jobId: clip.jobId,
          vodTitle: clip.vodTitle,
          vodThumbnailUrl: clip.vodThumbnailUrl,
          vodUrl: clip.vodUrl,
          clips: [],
          hasEditedClips: false,
        };
      }
      groups[clip.jobId].clips.push(clip);
      // Eğer bu clip edited tipindeyse hasEditedClips'i true yap
      if (clip.type === 'edited') {
        groups[clip.jobId].hasEditedClips = true;
      }
    });
    return Object.values(groups).sort((a, b) =>
        new Date(b.clips[0].createdAt).getTime() - new Date(a.clips[0].createdAt).getTime()
    );
  };

  const fetchClips = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<ClipGalleryItem[]>('/clips');
      const fetchedClips = response.data;
      // Burada mükerrer klip birleştirme mantığı vardı, şimdilik basitleştiriyoruz.
      // Gerekirse yeniden eklenebilir.
      const grouped = groupClipsByJob(fetchedClips);
      setJobGroups(grouped);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load clips.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  // 🚀 Listen for immediate clip creation events from Socket.IO
  useEffect(() => {
    const handleClipCreated = (event: CustomEvent) => {
      const { clip, jobId, clipIndex, totalClips } = event.detail;
      console.log(`[ClipGalleryPage] 🚀 New clip created for job ${jobId}: clip ${clipIndex}/${totalClips} - ${clip.title}`);
      
      // Convert backend clip format to frontend format
      const newClipItem: ClipGalleryItem = {
        id: clip.id,
        title: clip.title,
        thumbnailPath: clip.thumbnailPath,
        filePath: clip.filePath,
        jobId: clip.jobId,
        vodTitle: null, // Will be filled when job completes
        vodThumbnailUrl: null, // Will be filled when job completes
        vodUrl: null, // Will be filled when job completes
        llmOutputId: null,
        startTime: clip.startTime,
        endTime: clip.endTime,
        duration: clip.endTime - clip.startTime,
        createdAt: clip.createdAt,
        type: 'original',
        originalClipId: undefined
      };
      
      // Add to gallery immediately
      setJobGroups(prevJobGroups => {
        const newGroups = [...prevJobGroups];
        let groupFound = false;

        for (let i = 0; i < newGroups.length; i++) {
          if (newGroups[i].jobId === jobId) {
            const clipExists = newGroups[i].clips.some(c => c.id === clip.id);
            if (!clipExists) {
              newGroups[i].clips.unshift(newClipItem);
              console.log(`[ClipGalleryPage] ✅ Added clip to existing job group: ${clip.title}`);
            }
            groupFound = true;
            break;
          }
        }

        if (!groupFound) {
          // Create new group for this job
          newGroups.unshift({
            jobId: jobId,
            vodTitle: `Job ${jobId.slice(0, 8)}...`, // Temporary title
            vodThumbnailUrl: null,
            vodUrl: null,
            clips: [newClipItem],
            hasEditedClips: false, // Yeni clipler genelde original olur
          });
          console.log(`[ClipGalleryPage] 🚀 Created new job group for clip: ${clip.title}`);
        }
        
        return newGroups;
      });
    };

    window.addEventListener('clipCreated', handleClipCreated);
    
    return () => {
      window.removeEventListener('clipCreated', handleClipCreated);
    };
  }, []);

  // URL'den klip seçme mantığı, tüm klipler üzerinden arama yapacak şekilde güncellenmeli
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clipIdFromUrl = params.get('selectedClipId');
    if (clipIdFromUrl && jobGroups.length > 0) {
      const allClips = jobGroups.flatMap(g => g.clips);
      const clipToSelect = allClips.find(c => c.id === clipIdFromUrl);
      if (clipToSelect) {
        setSelectedClip(clipToSelect);
        setIsPopupOpen(true);
      } else {
        routerNavigate(location.pathname, { replace: true });
      }
    }
  }, [location.search, jobGroups, routerNavigate, location.pathname]);

  const { socket } = useSocket(); // Use the central socket instance

  // WebSocket logic now uses the socket from the context
  useEffect(() => {
    if (!socket) return undefined;

    const handleNewClip = (newClip: ClipGalleryItem) => {
      console.log('[ClipGalleryPage] Received new_clip_gallery_item:', newClip);
      setJobGroups(prevJobGroups => {
        const newGroups = [...prevJobGroups];
        let groupFound = false;

        for (let i = 0; i < newGroups.length; i++) {
          if (newGroups[i].jobId === newClip.jobId) {
            const clipExists = newGroups[i].clips.some(c => c.id === newClip.id);
            if (!clipExists) {
              newGroups[i].clips.unshift(newClip);
            }
            groupFound = true;
            break;
          }
        }

        if (!groupFound) {
          newGroups.unshift({
            jobId: newClip.jobId,
            vodTitle: newClip.vodTitle,
            vodThumbnailUrl: newClip.vodThumbnailUrl,
            vodUrl: newClip.vodUrl,
            clips: [newClip],
            hasEditedClips: newClip.type === 'edited',
          });
        } else {
          // Eğer yeni clip edited tipindeyse grubu güncelle
          if (newClip.type === 'edited') {
            for (let i = 0; i < newGroups.length; i++) {
              if (newGroups[i].jobId === newClip.jobId) {
                newGroups[i].hasEditedClips = true;
                break;
              }
            }
          }
        }
        
        return newGroups;
      });
    };

    socket.on('new_clip_gallery_item', handleNewClip);

    // No need to listen for 'connect' or 'disconnect' here, as the context handles it.
    // The error from the log was because this component was creating its own unauthenticated socket.
    // By using the context's socket, we leverage the already authenticated connection.

    return () => {
      socket.off('new_clip_gallery_item', handleNewClip);
    };
  }, [socket]);

  const handleClipClick = (clip: ClipGalleryItem) => {
    if (!isEditMode) {
      setSelectedClip(clip);
      setIsPopupOpen(true);
      const newSearch = new URLSearchParams(location.search);
      newSearch.set('selectedClipId', clip.id);
      routerNavigate(`${location.pathname}?${newSearch.toString()}`, { replace: true });
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedClip(null);
    routerNavigate(location.pathname, { replace: true });
  };

  const handleNavigate = (newIndex: number) => {
    const allClips = jobGroups.flatMap(g => g.clips).filter(c => c.type === activeCategory);
    if (newIndex >= 0 && newIndex < allClips.length) {
      setSelectedClip(allClips[newIndex]);
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    setSelectedClipIds([]);
  };

  const handleDeleteSelectedClips = async () => {
    // Silme mantığı buraya gelecek (mevcut koddan alınabilir)
  };

  const displayedJobGroups = jobGroups.map(group => ({
    ...group,
    clips: group.clips.filter(clip => clip.type === activeCategory)
  })).filter(group => group.clips.length > 0);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/70">Loading your clips...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Error Loading Clips</div>
          <p className="text-white/70">{error}</p>
        </div>
      </div>
    );
  }
  
  if (jobGroups.length === 0 && !isLoading) {
    return (
      <div 
        className="min-h-screen py-8 bg-background flex items-center justify-center"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '2rem 2rem',
        }}
      >
        <div className="text-center max-w-md mx-auto px-4">
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-500/20 to-slate-500/20 rounded-2xl opacity-30 blur-xl" />
            
            <div className="relative bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
              <div className="flex flex-col items-center text-center">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4">
                  <Film size={32} className="text-white/60" />
                </div>
                <h2 className="text-2xl font-semibold text-white mb-2">No clips found</h2>
                <p className="text-white/60 mb-6">Upload a VOD to create your first AI-generated clips</p>
                <Link
                  to="/"
                  className="
                    inline-flex items-center gap-2 px-6 py-3 rounded-xl
                    bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium
                    hover:from-blue-600 hover:to-cyan-600 shadow-lg hover:shadow-xl
                    transition-all duration-200 transform hover:scale-105
                  "
                >
                  <Film size={16} />
                  Create New Clips
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-8 bg-background"
      style={{
        backgroundImage: `
          linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: '2rem 2rem',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Glassmorphism Header */}
        <div className="relative group mb-8">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-2xl opacity-30 blur-xl group-hover:opacity-50 transition-opacity duration-300" />
          
          <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            <div className="p-6 md:p-8">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-white mb-2">Clip Gallery</h1>
                <p className="text-white/70">Discover and manage your AI-generated video clips</p>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Category Selector */}
        <div className="flex justify-center mb-8">
          <div className="relative bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-2 shadow-lg">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setActiveCategory('original')}
                className={`
                  flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold 
                  transition-all duration-200 relative z-10
                  ${activeCategory === 'original' 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                  }
                `}
              >
                <Film size={16} className="mr-2" />
                Raw Clips
              </button>
              <button
                onClick={() => setActiveCategory('edited')}
                className={`
                  flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold 
                  transition-all duration-200 relative z-10
                  ${activeCategory === 'edited' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                  }
                `}
              >
                <Sparkles size={16} className="mr-2" />
                Creations
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        {displayedJobGroups.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedJobGroups.map(group => (
              <JobClipGroup
                key={group.jobId}
                jobId={group.jobId}
                vodTitle={group.vodTitle}
                vodThumbnailUrl={group.vodThumbnailUrl}
                vodUrl={group.vodUrl}
                clipCount={group.clips.length}
                hasEditedClips={group.hasEditedClips}
                activeCategory={activeCategory}
              />
            ))}
          </div>
        ) : (
          !isLoading && (
            <div className="text-center py-16">
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-500/20 to-slate-500/20 rounded-2xl opacity-30 blur-xl" />
                
                <div className="relative bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4">
                      {activeCategory === 'original' ? <Film size={32} className="text-white/60" /> : <Sparkles size={32} className="text-white/60" />}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      No {activeCategory} clips found
                    </h3>
                    <p className="text-white/60 mb-6">
                      {activeCategory === 'original' 
                        ? 'Upload a VOD to create your first clips with AI detection'
                        : 'Use the clip editor to create your first custom video'
                      }
                    </p>
                    <Link
                      to="/"
                      className="
                        inline-flex items-center gap-2 px-6 py-3 rounded-xl
                        bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium
                        hover:from-blue-600 hover:to-cyan-600 shadow-lg hover:shadow-xl
                        transition-all duration-200 transform hover:scale-105
                      "
                    >
                      <Film size={16} />
                      Get Started
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ClipGalleryPage;